import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { showConfirmDialog } from '../dialogs/confirmDialog';
import { showErrorToast, showToast } from '../notifications/toast';
import { isEmbeddedEditor, isMinimalEditorChrome } from '../editorShell/editorChrome';
import { openDocumentEditorSession } from '../editorShell/editorOpenHelpers';
import type { EditorAccessMode } from '../editorShell/editorWorkspaceIds';
import {
  buildDocumentEditorSearch,
  documentEditorRoute
} from './documentEditorUrls';
import {
  isAllowedOfficeFileForKind,
  officeFileInputAccept,
  type DocumentEditorKind
} from './documentFileTypes';
import { uploadOfficeFileToUserStorage } from './uploadOfficeFileToUserStorage';
import type { DocumentOpenPickerSelection } from './DocumentOpenPickerPopup';

export type DocumentOpenSource = 'pc' | 'storage';

type RequestDirtyState = () => Promise<boolean>;

/**
 * Shared File → Open flow for document/spreadsheet editor shells:
 * From PC (upload to My Storage root) and From storage (picker).
 */
export function useDocumentEditorOpenFlow(options: {
  kind: DocumentEditorKind;
  mountId: string;
  path: string;
  requestDirtyState: RequestDirtyState;
}) {
  const { kind, mountId, path, requestDirtyState } = options;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const stayInEditor = isEmbeddedEditor(params) || isMinimalEditorChrome(params);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const confirmLeaveIfDirty = useCallback(async (): Promise<boolean> => {
    const dirty = await requestDirtyState();
    if (!dirty) return true;
    return showConfirmDialog({
      title: 'Discard unsaved changes?',
      message: 'You have unsaved changes. Opening another file will discard them. Continue?',
      confirmText: 'Open anyway',
      danger: true
    });
  }, [requestDirtyState]);

  const navigateToFile = useCallback(
    (nextMountId: string, nextPath: string, accessMode: EditorAccessMode = 'edit') => {
      if (nextMountId === mountId && nextPath === path) {
        showToast('That file is already open.', 'info');
        return;
      }
      if (stayInEditor) {
        const editorMode = accessMode === 'view' ? 'preview' : 'edit';
        navigate(
          `${documentEditorRoute(kind, editorMode)}?${buildDocumentEditorSearch(nextMountId, nextPath, params)}`
        );
        return;
      }
      openDocumentEditorSession({
        kind,
        mountId: nextMountId,
        path: nextPath,
        accessMode,
        preferredMode: 'inAppTab',
        navigate
      });
    },
    [kind, mountId, navigate, params, path, stayInEditor]
  );

  const openFromPc = useCallback(async () => {
    if (busy) return;
    const ok = await confirmLeaveIfDirty();
    if (!ok) return;
    const input = fileInputRef.current;
    if (!input) return;
    input.value = '';
    input.click();
  }, [busy, confirmLeaveIfDirty]);

  const openFromStorage = useCallback(async () => {
    if (busy) return;
    const ok = await confirmLeaveIfDirty();
    if (!ok) return;
    setPickerOpen(true);
  }, [busy, confirmLeaveIfDirty]);

  const handleOpenRequest = useCallback(
    (source: DocumentOpenSource) => {
      if (source === 'pc') void openFromPc();
      else void openFromStorage();
    },
    [openFromPc, openFromStorage]
  );

  const onFileInputChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      if (!isAllowedOfficeFileForKind(file.name, kind)) {
        showErrorToast(
          kind === 'spreadsheet'
            ? 'Choose a spreadsheet file (XLSX, XLS, XLSM, CSV, or TSV).'
            : 'Choose a document file (DOCX, DOC, or RTF).'
        );
        return;
      }
      setBusy(true);
      try {
        const uploaded = await uploadOfficeFileToUserStorage(file, kind);
        navigateToFile(uploaded.mountId, uploaded.relativePath, 'edit');
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Could not upload file.');
      } finally {
        setBusy(false);
      }
    },
    [kind, navigateToFile]
  );

  const onPickerOpen = useCallback(
    (selection: DocumentOpenPickerSelection) => {
      setPickerOpen(false);
      navigateToFile(
        selection.mountId,
        selection.path,
        selection.mode === 'ReadWrite' ? 'edit' : 'view'
      );
    },
    [navigateToFile]
  );

  const fileInputAccept = officeFileInputAccept(kind);

  return {
    pickerOpen,
    setPickerOpen,
    busy,
    fileInputRef,
    fileInputAccept,
    handleOpenRequest,
    onFileInputChange,
    onPickerOpen
  };
}
